import { Base } from '@studiometa/js-toolkit';
import { wpLocalizedApi } from '../../utils/index.js';

/**
 * @typedef {{
 *   $refs: {
 *     form: HTMLFormElement;
 *     'datepicker-content': HTMLElement;
 *   }
 * }} SearchComponentProps
 */

/**
 * SearchComponent component
 * @augments {Base<SearchComponentProps>}
 */
export default class SearchComponent extends Base {
  /**
   * Config.
   * @type {import('@studiometa/js-toolkit').BaseConfig}
   */
  static config = {
    name: 'Search',
    refs: ['form', 'datepicker-content'],
    options: {
      searchTermId: {
        type: Number,
        default: null,
      },
      datepickerContent: {
        type: String,
        default: '',
      },
      datepickerType: {
        type: String,
        default: 'green',
      },
    },
  };

  isLoading = false;

  isLoaded = false;

  hasBeenClicked = false;

  /**
   * Test if we are on the mobile version of the component or not.
   * @returns {boolean}
   */
  get isMobile() {
    return ['xxs', 'xs', 's'].includes(this.$services.get('resized').breakpoint);
  }

  /**
   * Test if the panel should be opened on mount.
   * @returns {boolean}
   */
  get shouldOpenOnMount() {
    return this.isMobile && this.hasBeenClicked;
  }

  /**
   * Fetch taxonomies.
   * @returns {Promise<void>}
   */
  async getData() {
    if (this.isLoading || this.isLoaded) {
      return Promise.resolve();
    }

    this.isLoading = true;

    const [trad, france, nearTo, envie, mount] = await Promise.all([
      wpLocalizedApi(`/wp-json/custom/weglot`),
      wpLocalizedApi('/wp-json/wp/v2/france?per_page=99&acf_format=standard&parent=0'),
      wpLocalizedApi('/wp-json/wp/v2/proche-de?per_page=99&acf_format=standard&parent=0'),
      wpLocalizedApi('/wp-json/wp/v2/envie?per_page=99&acf_format=standard&parent=0'),
      import(/* webpackPreload: true */ './mount.js').then((mod) => mod.mount),
    ]);

    mount(this, {
      france,
      nearTo,
      envie,
      trad,
      isOpen: this.shouldOpenOnMount,
    });

    this.isLoading = false;
    this.isLoaded = true;
  }

  /**
   * Get data when pointer is down on mobile.
   */
  onPointerdown() {
    this.hasBeenClicked = true;
    if (this.isMobile) {
      this.getData();
    }
  }

  /**
   * Instantiate the Vue app.
   */
  mounted() {
    if (!this.isMobile) {
      this.getData();
    }
  }

  /**
   * Get data on resize if needed.
   */
  resized() {
    if (!this.isMobile) {
      this.getData();
    }
  }
}
